import React, { useMemo } from 'react';
import ResourcesHeader from '@/components/image-generator/ResourcesContainer/ResourcesHeader';
import Container from '@/components/ui/Container';
import SidebarLayout from '@/components/layout/SidebarLayout';
import FilterSection from '@/components/image-generator/FilterSection/FilterSection';
import ImageLibrary from '@/components/image-generator/ImageLibrary/ImageLibrary';
import { useImageFilters } from '@/hooks/image-generator/image-filters';
import { useToast } from '@/hooks/errors/use-toast';
import { useResourceImageGeneration } from '@/hooks/image-generator/useImageGeneration';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';

const ImageGenerator = () => {
  const { toast } = useToast();
  
  // Get image filters state and handlers
  const {
    selectedType,
    styleOptions,
    selectedStyle,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    setSelectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    handleTypeChange,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    width,
    height,
    setWidth,
    setHeight,
    imageDescription,
    setImageDescription,
    debouncedImageDescription,
    isAdvancedMode,
    setIsAdvancedMode
  } = useImageFilters();
  
  // Function to handle width changes
  const handleWidthChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      setWidth(value);
    }
  };

  // Function to handle height changes
  const handleHeightChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      setHeight(value);
    }
  };
  
  // Handle image description changes
  const handleImageDescriptionChange = (value: string) => {
    setImageDescription(value);
    // Remove immediate setSearchTerm call - let the useEffect handle debounced updates
  };

  // Use the custom image generation hook
  const {
    isGenerating,
    isImageLoading,
    selectedImage,
    setSelectedImage,
    handleGenerateImage
  } = useResourceImageGeneration({
    imageDescription,
    width,
    height,
    selectedType,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    styleOptions,
    influenceOptions,
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions
  });

  // Memoize the sidebar content to prevent re-initialization during layout changes
  const sidebarContent = useMemo(() => (
    <FilterSection
      selectedType={selectedType}
      onTypeChange={handleTypeChange}
      styleOptions={styleOptions}
      selectedStyle={selectedStyle}
      onStyleChange={handleStyleChange}
      setSelectedStyle={setSelectedStyle}
      influenceOptions={influenceOptions}
      selectedInfluence={selectedInfluence}
      onInfluenceChange={handleInfluenceChange}
      setSelectedInfluence={setSelectedInfluence}
      colorOptions={colorOptions}
      onColorChange={handleColorChange}
      backgroundOptions={backgroundOptions}
      onBackgroundChange={handleBackgroundChange}
      selectedBackground={selectedBackground}
      environmentOptions={environmentOptions}
      onEnvironmentChange={handleEnvironmentChange}
      moodOptions={moodOptions}
      onMoodChange={handleMoodChange}
      imageDescription={imageDescription}
      onImageDescriptionChange={handleImageDescriptionChange}
      width={width}
      height={height}
      onWidthChange={handleWidthChange}
      onHeightChange={handleHeightChange}
      isGenerating={isGenerating}
      isImageLoading={isImageLoading}
      onGenerate={handleGenerateImage}
      isAdvancedMode={isAdvancedMode}
      setIsAdvancedMode={setIsAdvancedMode}
    />
  ), [
    selectedType,
    handleTypeChange,
    styleOptions,
    selectedStyle,
    handleStyleChange,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    handleInfluenceChange,
    setSelectedInfluence,
    colorOptions,
    handleColorChange,
    backgroundOptions,
    handleBackgroundChange,
    selectedBackground,
    environmentOptions,
    handleEnvironmentChange,
    moodOptions,
    handleMoodChange,
    imageDescription,
    handleImageDescriptionChange,
    width,
    height,
    handleWidthChange,
    handleHeightChange,
    isGenerating,
    isImageLoading,
    handleGenerateImage,
    isAdvancedMode,
    setIsAdvancedMode
  ]);

  return (
    <MobilePageWrapper>
      <Container>
        <ResourcesHeader />
        <div className="space-y-3 md:space-y-6">
          <SidebarLayout sidebar={sidebarContent}>
            <ImageLibrary 
              styleId={selectedStyle}
              influenceId={selectedInfluence}
              backgroundId={selectedBackground}
              environmentIds={environmentOptions.filter(o => o.checked).map(o => o.id)}
              moodIds={moodOptions.filter(o => o.checked).map(o => o.id)}
              colorIds={colorOptions.filter(o => o.checked).map(o => o.id)}
              imageType={selectedType}
              description={imageDescription}
              debouncedDescription={debouncedImageDescription}
              width={width}
              height={height}
              selectedImage={selectedImage}
              onImageSelect={setSelectedImage}
            />
          </SidebarLayout>
        </div>
      </Container>
    </MobilePageWrapper>
  );
};

export default ImageGenerator; 